'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';

import { Card, EmailConfidenceChip } from 'src/components/PulseUI';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { ReportDataIssueButton } from 'src/modules/Profile/components/Dialogs';
import { useLayoutControl } from 'src/context/layout';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { exists } from 'src/utils/exists/exists';
import { formatPhoneNumber } from 'src/utils/formatPhoneNumber/formatPhoneNumber';
import { createGoogleMapsUrl } from 'src/utils/createGoogleMapsLink';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';

interface DetailItem {
  label: string;
  value: string | null;
  type?: 'text' | 'link' | 'address';
  href?: string;
  showEmailChip?: boolean;
  addressLines?: string[];
}

const traumaLabelMap: Record<number, string> = {
  1: 'Level I',
  2: 'Level II',
  3: 'Level III',
  4: 'Level IV',
  5: 'Level V',
};

const buildAddressItems = (
  address: Record<string, any>,
  label: string
): DetailItem[] => {
  const items: DetailItem[] = [];
  const lines: string[] = [];

  if (address.practice_name) lines.push(address.practice_name);
  if (address.address_line_1) lines.push(address.address_line_1);
  if (address.address_line_2) lines.push(address.address_line_2);

  const cityStateZip = [
    address.city,
    address.state
      ? `${address.state} ${address.zip_code || ''}`.trim()
      : null,
  ]
    .filter(Boolean)
    .join(', ');
  if (cityStateZip) lines.push(cityStateZip);

  if (lines.length > 0) {
    const mapsUrl = createGoogleMapsUrl(address);
    items.push({
      label,
      value: lines.join(', '),
      type: 'address',
      href: mapsUrl,
      addressLines: lines,
    });
  }

  const phone = address.phone ? formatPhoneNumber(address.phone) : null;
  if (phone) items.push({ label: `${label} Phone`, value: phone });

  return items;
};

const buildHCPDetails = (entity: Record<string, any>): DetailItem[] => {
  const items: DetailItem[] = [];

  if (entity.id) items.push({ label: 'NPI', value: String(entity.id) });
  if (entity.name) items.push({ label: 'Name', value: entity.name });
  if (entity.specialty)
    items.push({ label: 'Specialty', value: entity.specialty });
  if (entity.phone) {
    const formatted = formatPhoneNumber(entity.phone);
    if (formatted) items.push({ label: 'Phone', value: formatted });
  }
  if (entity.email)
    items.push({
      label: 'Email',
      value: entity.email,
      showEmailChip: !!entity.email_source,
    });

  // Primary address
  if (entity.primary_address) {
    items.push(
      ...buildAddressItems(entity.primary_address, 'Primary Address')
    );
  }

  // Secondary addresses
  const secondaryAddresses = entity.secondary_addresses || [];
  secondaryAddresses.forEach((addr: Record<string, any>, i: number) => {
    const label =
      secondaryAddresses.length > 1
        ? `Secondary Address ${i + 1}`
        : 'Secondary Address';
    items.push(...buildAddressItems(addr, label));
  });

  return items;
};

const buildClinicDetails = (entity: Record<string, any>): DetailItem[] => {
  const items = buildHCPDetails(entity);

  const gpoNames = entity.gpos
    ?.map((gpo: { name?: string }) => gpo.name)
    .filter(Boolean)
    .join(', ');
  if (gpoNames) items.push({ label: 'GPO', value: gpoNames });

  return items;
};

const buildGroupDetails = (entity: Record<string, any>): DetailItem[] => {
  const items: DetailItem[] = [];

  if (entity.specialty)
    items.push({ label: 'Specialty', value: entity.specialty });
  if (entity.pac_id)
    items.push({ label: 'PAC ID', value: String(entity.pac_id) });
  if (entity.name) items.push({ label: 'Name', value: entity.name });

  return items;
};

const buildCenterDetails = (entity: Record<string, any>): DetailItem[] => {
  const items: DetailItem[] = [];

  if (entity.specialty)
    items.push({ label: 'Type of Service', value: entity.specialty });
  if (entity.id)
    items.push({ label: 'CMS Provider ID', value: String(entity.id) });
  if (entity.name) items.push({ label: 'Name', value: entity.name });

  const addr = entity.primary_address;
  if (addr) {
    items.push(...buildAddressItems(addr, 'Address'));

    const phone = addr.phone ? formatPhoneNumber(addr.phone) : null;
    if (phone && !items.some((i) => i.label === 'Address Phone'))
      items.push({ label: 'Phone', value: phone });
  }

  if (entity.website) {
    const href = entity.website.startsWith('http')
      ? entity.website
      : `http://${entity.website}`;
    items.push({
      label: 'Website',
      value: entity.website,
      type: 'link',
      href,
    });
  }

  if (exists(entity.alos_days))
    items.push({
      label: 'Avg Length of Stay',
      value: `${parseFloat(entity.alos_days).toFixed(1)} days`,
    });
  if (exists(entity.operating_room_count))
    items.push({
      label: 'Operating Rooms',
      value: String(entity.operating_room_count),
    });
  if (exists(entity.total_bed_count))
    items.push({
      label: 'Staffed Beds',
      value: String(entity.total_bed_count),
    });
  if (exists(entity.mac_carrier))
    items.push({ label: 'MAC', value: entity.mac_carrier });
  if (exists(entity.adult_trauma_level))
    items.push({
      label: 'Adult Trauma',
      value: traumaLabelMap[entity.adult_trauma_level] ?? null,
    });
  if (exists(entity.pediatric_trauma_level))
    items.push({
      label: 'Pediatric Trauma',
      value: traumaLabelMap[entity.pediatric_trauma_level] ?? null,
    });

  const gpoNames = entity.gpos
    ?.map((gpo: { name?: string }) => gpo.name)
    .filter(Boolean)
    .join(', ');
  if (gpoNames) items.push({ label: 'GPO', value: gpoNames });

  return items;
};

const buildSystemDetails = (entity: Record<string, any>): DetailItem[] => {
  const items: DetailItem[] = [];

  if (entity.name) items.push({ label: 'Name', value: entity.name });
  if (entity.id)
    items.push({ label: 'System ID', value: String(entity.id) });

  const addr = entity.primary_address;
  if (addr) {
    const cityState =
      addr.city && addr.state ? `${addr.city}, ${addr.state}` : null;
    if (cityState) items.push({ label: 'Location', value: cityState });

    const phone = addr.phone ? formatPhoneNumber(addr.phone) : null;
    if (phone) items.push({ label: 'Phone', value: phone });
  }

  if (entity.website) {
    const href = entity.website.startsWith('http')
      ? entity.website
      : `http://${entity.website}`;
    items.push({
      label: 'Website',
      value: entity.website,
      type: 'link',
      href,
    });
  }

  if (exists(entity.total_sites))
    items.push({ label: 'Total Sites', value: String(entity.total_sites) });
  if (exists(entity.total_bed_count))
    items.push({
      label: 'Staffed Beds',
      value: String(entity.total_bed_count),
    });

  return items;
};

const BUILDERS: Record<
  string,
  (entity: Record<string, any>) => DetailItem[]
> = {
  hcp: buildHCPDetails,
  clinic: buildClinicDetails,
  center: buildCenterDetails,
  group_pac: buildGroupDetails,
  system: buildSystemDetails,
};

const CompactDetails: React.FC = () => {
  const { entityDetails } = useProfile();
  const { setShowReportDataIssueDialog } = useLayoutControl();

  const { providerId, profileType: entityType } = getEntityIdentifiers(entityDetails);
  const builder = BUILDERS[entityType] || buildHCPDetails;

  const details = useMemo(() => {
    if (!entityDetails) return [];
    return builder(entityDetails).filter((item) => item.value !== null);
  }, [entityDetails, builder]);

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="compact-details"
    >
      <WidgetHeader fallbackTitle="Details" />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {details.length === 0 ? (
          <WidgetEmpty data-testid="compact-details-empty" variant="full" />
        ) : (
          <div
            className="flex flex-col"
            role="list"
            aria-label="Details"
            data-testid="compact-details-list"
          >
            {details.map((item, index) => (
              <div
                key={`${item.label}-${index}`}
                className="flex flex-col py-1.5 border-b border-neutral-100 last:border-b-0"
                role="listitem"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs text-tertiary">{item.label}</span>
                  {item.showEmailChip && entityDetails?.email_source && (
                    <EmailConfidenceChip
                      source={entityDetails.email_source}
                    />
                  )}
                </div>
                {item.type === 'link' && item.href ? (
                  <Link
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors truncate"
                    aria-label={`Visit ${item.value}`}
                  >
                    {item.value}
                  </Link>
                ) : item.type === 'address' && item.href ? (
                  <Link
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors"
                    aria-label={`View address on Google Maps`}
                  >
                    <div className="flex flex-col">
                      {(item.addressLines || []).map((line, i) => (
                        <span key={i}>{line}</span>
                      ))}
                    </div>
                  </Link>
                ) : (
                  <span className="text-sm font-semibold text-primary truncate">
                    {item.value}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ReportDataIssueButton
        onClick={() =>
          setShowReportDataIssueDialog({
            show: true,
            providerId,
            providerType: entityType,
          })
        }
      />
    </Card>
  );
};

export default CompactDetails;
